import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { post } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { AuthLayout } from "@/pages/auth/AuthLayout";
import { Field } from "@/components/common/FormField";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

const strongEnough = (pwd) => pwd.length >= 8 && /[A-Za-z]/.test(pwd) && /\d/.test(pwd);

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const errs = {};
    if (!strongEnough(password)) errs.password = t("auth.passwordRules");
    if (password !== confirm) errs.confirm = t("auth.passwordMismatch");
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await post("/auth/reset-password", { token: token.trim(), new_password: password });
      navigate("/login", { replace: true, state: { resetDone: true } });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={t("auth.resetTitle")}
      subtitle={t("auth.resetSubtitle")}
      footer={<Link to="/login" className="og-focus font-medium text-primary hover:underline" data-testid="reset-back-to-login">{t("auth.backToLogin")}</Link>}
    >
      <form onSubmit={submit} className="space-y-4" noValidate data-testid="reset-form">
        {error && <Alert className="border-rose-200 bg-rose-50 text-rose-900" data-testid="reset-error"><AlertDescription>{error}</AlertDescription></Alert>}
        <Field id="reset-token" label={t("auth.resetToken")} hint={t("auth.resetTokenHint")}>
          <Input id="reset-token" required value={token} onChange={(e) => setToken(e.target.value)} className="font-mono text-xs" data-testid="reset-token-input" />
        </Field>
        <Field id="reset-password" label={t("auth.newPassword")} hint={t("auth.passwordRules")} error={fieldErrors.password}>
          <Input id="reset-password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" aria-invalid={!!fieldErrors.password} />
        </Field>
        <Field id="reset-confirm" label={t("auth.passwordConfirm")} error={fieldErrors.confirm}>
          <Input id="reset-confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-password-confirm-input" aria-invalid={!!fieldErrors.confirm} />
        </Field>
        <Button type="submit" className="h-10 w-full" disabled={busy || !token || !password || !confirm} data-testid="reset-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {t("auth.resetButton")}
        </Button>
      </form>
    </AuthLayout>
  );
}
